// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, AppFooter, useCurrentUser, apiErrorMessage } from "./common";
import { resourceSearch, referrals, api } from "../api";
import { LocationPicker } from "../components/naver/LocationPicker";

// 자원은행 조건검색 (/resources) — 공무원이 "이 조건에 지금 가능한 기관"을 찾는 화면.
//
// 목표는 문서의 "1~3분 내 후보 확인"이다. 그래서:
//   · 조건은 한 줄로 펼쳐 두고, 바꾸면 바로 다시 찾는다(모달·마법사 없음).
//   · 후보마다 **왜 여기 있는지 한 줄**(reasoning)과 차원별 점수 막대를 함께 보여준다.
//   · 정렬 가중치를 화면에 공개한다 — "왜 이 순서인가"를 설명할 수 없는 랭킹은 쓰지 않는다.
//
// ⚠️ 케어매니저 COD 의 "AI 분석 중" 연출을 여기 붙이지 말 것. 저기선 연출이지만
//    여기선 3분 안에 끝내야 하는 업무 흐름과 정면으로 충돌한다.
//
// ⚠️ 이 화면은 **대상자를 API 에 보내지 않는다.** 조건만 보내고, 대상자와의 결합은
//    화면(사람) 쪽에서만 일어난다. 자원/대상자 논리 분리 계약.

const STATUS_COLOR = {
  available: "green", limited: "amber", unavailable: "rose",
  stale: "orange", unknown: "gray", suspended: "gray",
};
const DIM_LABEL = {
  availability: "가용", conditions: "조건", freshness: "신선도", distance: "거리",
};

export function ResourceSearch() {
  const { user, loaded } = useCurrentUser();
  const [catalog, setCatalog] = React.useState(null);
  const [form, setForm] = React.useState({
    service_code: "", dong: "", needed_from: "",
    required_tags: "", preferred_tags: "", limit: 5,
  });
  // 기준 위치(선택). 안 찍으면 거리 차원은 **적용하지 않는다** — 좌표 없이 중립값을
  // 넣으면 "거리를 반영한 순위"처럼 보이는데 실제로는 아무 영향이 없어 오해를 만든다.
  const [point, setPoint] = React.useState(null);
  // 의뢰 다이얼로그. **검색은 케이스를 모르지만, 의뢰는 케이스를 고른다** —
  // 자원/대상자 결합은 검색 API 가 아니라 이 화면(사람)에서만 일어난다.
  const [referTo, setReferTo] = React.useState(null);
  const [showMap, setShowMap] = React.useState(false);
  const [res, setRes]   = React.useState(null);
  // 이번 결과가 기준 위치를 반영했는지. 결과 표시에 그대로 쓴다(폼을 나중에 바꿔도 결과는 그대로).
  const [usedPoint, setUsedPoint] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr]   = React.useState(null);

  // 급여 카탈로그는 설정이 단일 출처(운영자가 라벨을 바꿔도 코드로 이어진다).
  React.useEffect(() => {
    fetch("/api/v1/settings/care_service_types", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j) setCatalog(j); })
      .catch(() => {});
  }, []);

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const search = React.useCallback(async () => {
    if (!form.service_code) { setErr("급여 종류를 선택해주세요."); return; }
    setBusy(true); setErr(null);
    try {
      const tags = (s) => s.split(/[,\s]+/).map(t => t.trim()).filter(Boolean);
      setRes(await resourceSearch.find({
        service_code:   form.service_code,
        dong:           form.dong,
        needed_from:    form.needed_from,
        required_tags:  tags(form.required_tags),
        preferred_tags: tags(form.preferred_tags),
        limit:          form.limit,
        lat:            point?.lat,
        lng:            point?.lng,
      }));
      setUsedPoint(!!point);
    } catch (e) {
      setErr(apiErrorMessage(e)); setRes(null);
    } finally { setBusy(false); }
  }, [form, point]);

  const codes = React.useMemo(() => {
    if (!catalog) return [];
    return Object.entries(catalog).flatMap(([group, items]) =>
      (items || []).map(it => ({ ...it, group })));
  }, [catalog]);

  return (
    <div className="app">
      <div className="shell">
        <Sidebar active="resources" user={user}/>
        <div className="main">
          <Topbar title="자원 조건검색" sub="조건에 맞는 기관을 찾습니다 · 후보 제시까지, 자동 배정은 하지 않습니다" user={user}/>

          <div className="content">
            {/* ── 조건 ─────────────────────────────────────── */}
            <div className="card" style={{padding:18, marginBottom:16}}>
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))', gap:12}}>
                <Field label="급여 종류" required>
                  <select className="select" value={form.service_code}
                          onChange={e => set('service_code')(e.target.value)} style={{width:'100%'}}>
                    <option value="">선택하세요</option>
                    {codes.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="행정동" hint="비우면 구역 제한 없이">
                  <input className="input" value={form.dong} placeholder="예: 진접읍"
                         onChange={e => set('dong')(e.target.value)} style={{width:'100%'}}/>
                </Field>
                <Field label="개시 희망일">
                  <input className="input" type="date" value={form.needed_from}
                         onChange={e => set('needed_from')(e.target.value)} style={{width:'100%'}}/>
                </Field>
                <Field label="필수 조건" hint="전부 갖춘 곳만">
                  <input className="input" value={form.required_tags} placeholder="예: 치매, 와상"
                         onChange={e => set('required_tags')(e.target.value)} style={{width:'100%'}}/>
                </Field>
                <Field label="선호 조건" hint="갖출수록 가점">
                  <input className="input" value={form.preferred_tags} placeholder="예: 호스피스"
                         onChange={e => set('preferred_tags')(e.target.value)} style={{width:'100%'}}/>
                </Field>
                <Field label="후보 수">
                  <select className="select" value={form.limit}
                          onChange={e => set('limit')(Number(e.target.value))} style={{width:'100%'}}>
                    {[3,5,10].map(n => <option key={n} value={n}>{n}곳</option>)}
                  </select>
                </Field>
              </div>
              <div style={{marginTop:12}}>
                <button type="button" onClick={() => setShowMap(v => !v)}
                  style={{background:'none', border:'none', cursor:'pointer', padding:0,
                          fontSize:11.5, fontWeight:700, color:'var(--ink-3)'}}>
                  기준 위치 {point ? `· 지정됨 (${point.lat.toFixed(4)}, ${point.lng.toFixed(4)})` : '· 미지정 — 거리 미적용'} {showMap ? '▲' : '▼'}
                </button>
                {showMap && (
                  <div style={{marginTop:8}}>
                    <div className="muted" style={{fontSize:11, marginBottom:6}}>
                      지도를 눌러 기준 위치를 찍으면 거리 점수가 적용됩니다. 안 찍으면 거리는 순위에 반영되지 않습니다.
                    </div>
                    <LocationPicker value={point} onChange={setPoint} height={200}/>
                    {point && (
                      <button type="button" onClick={() => setPoint(null)}
                        style={{marginTop:6, background:'none', border:'none', cursor:'pointer', padding:0,
                                fontSize:11.5, fontWeight:700, color:'var(--rose-600)'}}>
                        기준 위치 지우기
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div style={{display:'flex', alignItems:'center', gap:12, marginTop:14}}>
                <button className="btn btn-primary" onClick={search} disabled={busy}
                        style={{padding:'9px 20px'}}>
                  {busy ? '찾는 중…' : '후보 찾기'}
                </button>
                {err && <span style={{fontSize:12.5, color:'var(--rose-600)', fontWeight:600}}>{err}</span>}
              </div>
            </div>

            {/* ── 결과 ─────────────────────────────────────── */}
            {res && (
              <>
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between',
                             flexWrap:'wrap', gap:10, marginBottom:12}}>
                  <div style={{fontSize:13.5, fontWeight:700}}>
                    조건에 맞는 곳 {res.total}곳
                    {res.total > res.candidates.length &&
                      <span className="muted" style={{fontWeight:500}}> · 상위 {res.candidates.length}곳 표시</span>}
                  </div>
                  {/* 문서 FR-011: 정렬 기준을 화면에 공개한다. */}
                  <div className="muted" style={{fontSize:11.5, display:'flex', gap:8, alignItems:'center'}}>
                    <span style={{fontWeight:700}}>정렬 기준</span>
                    {Object.entries(res.weights || {}).map(([k, v]) => {
                      const off = k === 'distance' && !usedPoint;
                      return (
                        <span key={k} title={off ? '기준 위치를 찍지 않아 거리는 순위에 반영되지 않았습니다' : undefined}
                          style={{padding:'1px 7px', background:'var(--bg-sub)',
                                  border:'1px solid var(--border)', borderRadius:99,
                                  opacity: off ? 0.45 : 1,
                                  textDecoration: off ? 'line-through' : 'none'}}>
                          {DIM_LABEL[k] || k} {v}%
                        </span>
                      );
                    })}
                    <span>· 유효기간 {res.freshness_days}일</span>
                  </div>
                </div>

                {res.candidates.length === 0 && (
                  <div className="card" style={{padding:'34px 20px', textAlign:'center'}}>
                    <div style={{fontSize:14, fontWeight:700, marginBottom:6}}>조건에 맞는 기관이 없습니다</div>
                    <div className="muted" style={{fontSize:12.5, lineHeight:1.6}}>
                      필수 조건을 줄이거나 행정동·개시 희망일을 넓혀보세요.<br/>
                      기관이 아직 가용성을 보고하지 않았을 수도 있습니다.
                    </div>
                  </div>
                )}

                <div style={{display:'flex', flexDirection:'column', gap:11}}>
                  {res.candidates.map((c, i) => <CandidateCard key={c.organization_service_id} c={c} rank={i+1} usedPoint={usedPoint} onRefer={() => setReferTo(c)}/>)}
                </div>
              </>
            )}

            {!res && loaded && (
              <div className="card" style={{padding:'40px 20px', textAlign:'center'}}>
                <div className="muted" style={{fontSize:13}}>
                  급여 종류를 고르고 <b>후보 찾기</b>를 누르면 지금 가능한 기관을 보여줍니다.
                </div>
              </div>
            )}
          </div>
          {referTo && <ReferDialog candidate={referTo} onClose={() => setReferTo(null)}/>}
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label style={{display:'flex', flexDirection:'column', gap:5}}>
      <span className="muted" style={{fontSize:11, fontWeight:700}}>
        {label}{required && <span style={{color:'var(--orange-600)'}}> *</span>}
        {hint && <span style={{fontWeight:500}}> · {hint}</span>}
      </span>
      {children}
    </label>
  );
}

function CandidateCard({ c, rank, usedPoint, onRefer }) {
  const color = STATUS_COLOR[c.effective_status] || "gray";
  return (
    <div className="card" style={{padding:'15px 18px'}}>
      <div style={{display:'flex', alignItems:'flex-start', gap:12}}>
        <div style={{
          width:28, height:28, borderRadius:9, flex:'0 0 auto',
          background:'var(--bg-sub)', border:'1px solid var(--border)',
          display:'grid', placeItems:'center', fontSize:12.5, fontWeight:800, color:'var(--ink-2)',
        }}>{rank}</div>

        <div style={{flex:1, minWidth:0}}>
          <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
            <span style={{fontSize:14.5, fontWeight:800}}>{c.organization_name}</span>
            <span className={"badge badge-" + color} style={{fontSize:11}}>{c.status_label}</span>
            <span className="muted" style={{fontSize:11.5}}>{c.service_label}</span>
          </div>
          {/* 노출 사유 한 줄 — 왜 이 후보가 여기 있는지. */}
          <div style={{fontSize:12.5, color:'var(--ink-2)', marginTop:3}}>{c.reasoning}</div>
          <div className="muted" style={{fontSize:11.5, marginTop:3}}>
            {c.address || '주소 미상'}
            {c.phone ? ` · ${c.phone}` : ''}
            {c.capacity_total ? ` · 정원 ${c.capacity_total}` : ''}
          </div>
          {(c.matched_tags?.length > 0 || c.missing_tags?.length > 0) && (
            <div style={{display:'flex', gap:5, flexWrap:'wrap', marginTop:7}}>
              {c.matched_tags.map(t => (
                <span key={t} className="badge badge-green" style={{fontSize:10.5}}>{t}</span>
              ))}
              {c.missing_tags.map(t => (
                <span key={t} style={{fontSize:10.5, padding:'2px 8px', borderRadius:99,
                       border:'1px dashed var(--border)', color:'var(--ink-3)'}}>{t} 미확인</span>
              ))}
            </div>
          )}
        </div>

        <div style={{flex:'0 0 168px'}}>
          <div style={{display:'flex', alignItems:'baseline', justifyContent:'flex-end', gap:5}}>
            <span className="mono" style={{fontSize:20, fontWeight:800}}>{c.score}</span>
            <span className="muted" style={{fontSize:11}}>점</span>
          </div>
          <button className="btn btn-primary" onClick={onRefer}
                  style={{width:'100%', marginTop:7, padding:'6px 0', fontSize:12}}>
            의뢰 보내기
          </button>
          <div style={{display:'flex', flexDirection:'column', gap:3, marginTop:6}}>
            {Object.entries(c.scores || {}).map(([k, v]) => {
              // 기준 위치를 안 찍었으면 거리 점수는 전 후보가 같은 중립값이라 순위에
              // 영향이 없다. 막대를 그대로 보여주면 "거리를 봤다"는 오해를 주므로 흐린다.
              const off = k === 'distance' && !usedPoint;
              return (
                <div key={k} style={{display:'flex', alignItems:'center', gap:6, opacity: off ? 0.4 : 1}}>
                  <span className="muted" style={{fontSize:10, width:34, flex:'0 0 auto'}}>{DIM_LABEL[k] || k}</span>
                  <span style={{flex:1, height:5, background:'var(--bg-sub)', borderRadius:99, overflow:'hidden'}}>
                    <span style={{display:'block', height:'100%', width:`${off ? 0 : Math.max(0, Math.min(100, v))}%`,
                                  background:'var(--orange-400, var(--orange-300))'}}/>
                  </span>
                  <span className="mono muted" style={{fontSize:10, width:22, textAlign:'right'}}>{off ? '—' : v}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// 후보 하나를 골라 실제 의뢰를 보내는 다이얼로그.
// 케이스 선택이 여기 있는 이유: 검색 API 는 대상자를 모르고, 결합은 사람이 하는 이 지점에서만
// 일어나야 한다(자원/대상자 논리 분리).
function ReferDialog({ candidate, onClose }) {
  const [cases, setCases] = React.useState(null);
  const [caseId, setCaseId] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    // 의뢰 대상이 될 수 있는 케이스 — 아직 진행 중인 것만.
    api.list("cases", { status: "matching" })
      .then(d => setCases(d.cases || d || []))
      .catch(e => setErr(apiErrorMessage(e)));
  }, []);

  const submit = async () => {
    if (!caseId) { setErr("의뢰할 케이스를 선택해주세요."); return; }
    setBusy(true); setErr(null);
    try {
      await referrals.send({
        case_record_id: Number(caseId),
        organization_service_id: candidate.organization_service_id,
        note: note || undefined,
      });
      setDone(true);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(42,36,31,0.4)', display:'grid',
              placeItems:'center', zIndex:80, padding:20}}>
      <div className="card" onClick={e => e.stopPropagation()}
        style={{width:'min(520px,100%)', padding:22, maxHeight:'86vh', overflow:'auto'}}>
        <div className="h3" style={{marginBottom:2}}>서비스 의뢰</div>
        <div className="muted" style={{fontSize:12, marginBottom:16}}>
          {candidate.organization_name} · {candidate.service_label} · {candidate.status_label}
        </div>

        {done ? (
          <>
            <div style={{padding:'18px 0', fontSize:13.5, fontWeight:700, color:'var(--green-700)'}}>
              의뢰를 보냈습니다. 기관 응답을 기다립니다.
            </div>
            <div className="muted" style={{fontSize:12, marginBottom:16}}>
              기관이 연락·수용·거절로 답하면 알림으로 알려드립니다.
            </div>
            <button className="btn btn-primary" onClick={onClose} style={{padding:'9px 20px'}}>닫기</button>
          </>
        ) : (
          <>
            <label style={{display:'flex', flexDirection:'column', gap:5, marginBottom:12}}>
              <span className="muted" style={{fontSize:11, fontWeight:700}}>
                의뢰할 케이스 <span style={{color:'var(--orange-600)'}}>*</span>
              </span>
              <select className="select" value={caseId} onChange={e => setCaseId(e.target.value)}>
                <option value="">선택하세요</option>
                {(cases || []).map(c => (
                  <option key={c.id} value={c.id}>
                    {c.elderly?.name || c.care_manager_name || `케이스 #${c.id}`}
                    {c.elderly?.age ? ` · ${c.elderly.age}세` : ''}
                  </option>
                ))}
              </select>
            </label>

            <label style={{display:'flex', flexDirection:'column', gap:5, marginBottom:16}}>
              <span className="muted" style={{fontSize:11, fontWeight:700}}>요청 사항</span>
              <textarea className="input" rows={3} value={note} maxLength={300}
                        placeholder="예: 주 3회 오전 방문 희망, 휠체어 이동 지원 필요"
                        onChange={e => setNote(e.target.value)}/>
            </label>

            {err && <div style={{fontSize:12.5, color:'var(--rose-600)', fontWeight:600, marginBottom:12}}>{err}</div>}

            <div style={{display:'flex', gap:8}}>
              <button className="btn btn-primary" onClick={submit} disabled={busy}
                      style={{padding:'9px 20px'}}>{busy ? '보내는 중…' : '의뢰 보내기'}</button>
              <button className="btn" onClick={onClose} style={{padding:'9px 18px'}}>취소</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
